import { type PropsWithChildren, type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, NavLink, Navigate, matchPath, useLocation, useMatches } from "react-router-dom";
import { motion, useMotionValueEvent, useScroll, useTransform } from "motion/react";
import { Icon, IconArrowLeft } from "@tabler/icons-react";
import { CenteredLayout } from "./CenteredLayout";
import { Heading } from "./Heading";
import { classNames } from "../utilities/classNames";
import { pillSpring } from "./frame/frameMotion";
import { useFrameMenu } from "./frame/useFrameMenu";
import { useEdgeFade } from "../hooks/useEdgeFade";

export interface Tab {
    to: string;
    label: string;
    icon?: Icon;
    disabled?: boolean;
    end?: boolean;

    /** Drawn after the label, such as how many notes there are */
    count?: number;
}

/** Where the bar's back arrow goes; `to` resolves as `BackLink`'s does, defaulting to `".."` */
export interface HeaderBack {
    to?: string;
    label: string;
}

type Status = "success" | "danger" | "warning" | "info";

interface Props {
    heading: ReactNode;

    /** Use to set page title if a complex `heading` is set */
    plainTextHeading?: string;

    /** What the collapsed bar shows, where the heading carries more than the page title should */
    barTitle?: string;
    barSubtitle?: string;

    headerArea?: ReactNode;

    /** Left out, the arrow goes to the parent route crumb where the area has crumbs; `null` has none */
    back?: HeaderBack | null;

    /** Actions kept in the bar, and so in view however far the page scrolls */
    contextArea?: ReactNode;

    tabs?: Tab[];

    fullWidth?: boolean;

    status?: Status;
}

export const goToFirstTab = Symbol("goToFirstTab");

const TabCount = ({ count }: { count: number }) => (
    <span className="min-w-5 h-5 px-1.5 rounded-full bg-base-content/10 text-xs font-bold grid place-items-center">
        {count}
    </span>
);

/** Held until the page scrolls under the bar, then the bar takes the frame's shade */
const BAR_TINT: Record<Status, string> = {
    success: "bg-green-50 dark:bg-green-950",
    danger: "bg-red-50 dark:bg-red-950",
    warning: "bg-yellow-50 dark:bg-yellow-950",
    info: "bg-blue-50 dark:bg-blue-950",
};

const HEADING_TINT: Record<Status, string> = {
    success: "bg-gradient-to-b from-green-50 dark:from-green-950",
    danger: "bg-gradient-to-b from-red-50 dark:from-red-950",
    warning: "bg-gradient-to-b from-yellow-50 dark:from-yellow-950",
    info: "bg-gradient-to-b from-blue-50 dark:from-blue-950",
};

const matchHandleIsCrumb = (handle: unknown): handle is { crumb: string } =>
    typeof handle === "object" && handle !== null && "crumb" in handle;

/** The route crumb above this screen's, for the areas whose routes carry crumbs */
const useParentCrumb = (): HeaderBack | undefined => {
    const crumbs = useMatches().filter((match) => matchHandleIsCrumb(match.handle));
    const parent = crumbs.at(-2);

    if (!parent || !matchHandleIsCrumb(parent.handle)) return undefined;

    return { to: parent.pathname, label: `Back to ${parent.handle.crumb}` };
};

/**
 * The icon the frame shows for the screen, where the screen is one the menu leads to. An
 * exact match first, so a ministry shows its own icon rather than the Ministries one.
 */
const usePlaceIcon = (pathname: string): Icon | undefined => {
    const { main, groups, account } = useFrameMenu();
    const links = [...main, ...groups.flatMap((group) => [...group.links, ...(group.inactive ?? [])]), ...account];

    return (
        links.find((link) => matchPath({ path: link.to, end: true }, pathname)) ??
        main.find((item) => matchPath({ path: item.to, end: item.bottomEnd ?? false }, pathname))
    )?.icon;
};

export const HeadedLayout = (props: PropsWithChildren<Props>) => {
    const matches = useMatches();
    const { pathname } = useLocation();
    const parentCrumb = useParentCrumb();
    const PlaceIcon = usePlaceIcon(pathname);

    const barRef = useRef<HTMLDivElement>(null);
    const headingRef = useRef<HTMLDivElement>(null);
    const tabsHomeRef = useRef<HTMLDivElement>(null);
    const tabsRef = useRef<HTMLElement>(null);
    const tabsScrollRef = useRef<HTMLDivElement>(null);
    const tabListRef = useRef<HTMLUListElement>(null);

    /** Set when a tab is chosen with the header collapsed, so the next tab opens collapsed too */
    const keepCollapsedAt = useRef<number | null>(null);

    const [scrolled, setScrolled] = useState(false);
    const [stuck, setStuck] = useState(false);
    const [titleRange, setTitleRange] = useState<[number, number]>([40, 80]);
    const [underline, setUnderline] = useState<{ x: number; width: number } | null>(null);

    const { scrollY } = useScroll();
    const titleOpacity = useTransform(scrollY, titleRange, [0, 1]);
    const titleY = useTransform(scrollY, titleRange, [8, 0]);

    const showTabs = !!props.tabs && props.tabs.length > 1;
    const back = props.back === undefined ? parentCrumb : props.back;

    useEdgeFade(tabsScrollRef, showTabs);
    const barTitle = props.barTitle ?? props.plainTextHeading ?? (typeof props.heading === "string" ? props.heading : undefined);

    const updateStuck = useCallback(() => {
        const bar = barRef.current?.getBoundingClientRect();
        const tabs = tabsRef.current?.getBoundingClientRect();

        setStuck(!!bar && !!tabs && tabs.top <= bar.bottom + 0.5);
    }, []);

    useMotionValueEvent(scrollY, "change", (y) => {
        setScrolled(y > 4);
        updateStuck();
    });

    /**
     * The small title fades in over the stretch where the large heading passes under the
     * bar. Measured again whenever the heading block changes size, as a skeleton heading
     * is replaced by the loaded one.
     */
    useLayoutEffect(() => {
        const heading = headingRef.current;
        if (!heading) return;

        const measure = () => {
            const barBottom = barRef.current?.getBoundingClientRect().bottom ?? 64;
            const rect = heading.getBoundingClientRect();
            const top = rect.top + window.scrollY - barBottom;

            setTitleRange([Math.max(0, top + rect.height * 0.25), Math.max(1, top + rect.height * 0.7)]);
        };

        measure();

        const observer = new ResizeObserver(measure);
        observer.observe(heading);

        return () => observer.disconnect();
    }, []);

    /**
     * The tabs' underline is one element moved along x only. A shared-layout underline
     * measures against the page, so scrolling back after a tab change made it jump.
     */
    useLayoutEffect(() => {
        const list = tabListRef.current;
        if (!list) return;

        const place = () => {
            const current = list.querySelector<HTMLElement>("[aria-current='page']");
            if (!current) return setUnderline(null);

            /** Reaches 4px past the label each side, whatever the tab's padding at this width */
            const inset = parseFloat(getComputedStyle(current).paddingLeft) - 4;

            setUnderline({ x: current.offsetLeft + inset, width: current.offsetWidth - inset * 2 });
        };

        place();

        const observer = new ResizeObserver(place);
        observer.observe(list);

        return () => observer.disconnect();
    }, [pathname, showTabs]);

    useEffect(() => {
        if (keepCollapsedAt.current !== null) {
            window.scrollTo({ top: keepCollapsedAt.current });
            keepCollapsedAt.current = null;
        }

        const current = tabsRef.current?.querySelector<HTMLElement>("[aria-current='page']");
        const row = tabsScrollRef.current;

        if (current && row) {
            row.scrollTo({ left: current.offsetLeft - (row.clientWidth - current.offsetWidth) / 2, behavior: "smooth" });
        }

        updateStuck();
    }, [pathname, updateStuck]);

    const onTabClick = () => {
        const home = tabsHomeRef.current?.getBoundingClientRect().top;
        const barBottom = barRef.current?.getBoundingClientRect().bottom;
        if (home === undefined || barBottom === undefined) return;

        const target = window.scrollY + home - barBottom;

        keepCollapsedAt.current = window.scrollY >= target - 1 ? target : null;
    };

    const lastMatch = matches[matches.length - 1];

    if (props.tabs && lastMatch && lastMatch.handle === goToFirstTab) {
        return <Navigate to={props.tabs[0].to} replace />;
    }

    return (
        <>
            <div
                ref={barRef}
                data-app-bar
                data-route-tabs={showTabs || undefined}
                className={classNames(
                    "sticky top-[var(--frame-inset)] z-[45] flex items-center gap-1 box-content h-16 pt-[env(safe-area-inset-top)] px-1 lg:px-3 lg:rounded-t-3xl transition-colors duration-200",
                    scrolled ? "bg-frame" : props.status ? BAR_TINT[props.status] : "bg-base-200"
                )}
            >
                {/* A screen with nowhere to go back to leads with its own place, as the nav bar shows it */}
                {!back && PlaceIcon && (
                    <span aria-hidden="true" className="grid place-items-center shrink-0 w-12 h-12">
                        <span className="grid place-items-center w-10 h-10 rounded-full bg-indicator text-on-indicator">
                            <PlaceIcon className="w-[1.35rem] h-[1.35rem]" />
                        </span>
                    </span>
                )}

                {back && (
                    <Link
                        to={back.to || ".."}
                        aria-label={back.label}
                        title={back.label}
                        className="grid place-items-center shrink-0 w-12 h-12 rounded-full transition-all hover:bg-base-content/10 active:rounded-2xl"
                    >
                        <IconArrowLeft className="w-6 h-6" />
                    </Link>
                )}

                <motion.div
                    className={classNames("min-w-0 grow leading-tight", { "pl-3": !back && !PlaceIcon })}
                    style={{ opacity: titleOpacity, y: titleY }}
                    aria-hidden="true"
                >
                    {barTitle && <p className="truncate text-lg font-bold">{barTitle}</p>}

                    {props.barSubtitle && <p className="truncate text-xs text-base-content/70">{props.barSubtitle}</p>}
                </motion.div>

                {props.contextArea && <div className="flex items-center gap-1 shrink-0 pr-2">{props.contextArea}</div>}
            </div>

            <header
                ref={headingRef}
                className={classNames("px-4 pt-1 pb-4 lg:px-10 lg:pb-6", props.status && HEADING_TINT[props.status])}
            >
                <div className="max-w-screen-lg mx-auto">
                    {props.heading && <HeadedLayout.Heading>{props.heading}</HeadedLayout.Heading>}

                    {props.headerArea}
                </div>
            </header>

            {showTabs && props.tabs && (
                <>
                    <div ref={tabsHomeRef} aria-hidden="true" />

                    <nav
                        ref={tabsRef}
                        className={classNames(
                            "sticky top-[calc(var(--frame-inset)+4rem+env(safe-area-inset-top))] z-40 border-b border-base-300 transition-colors duration-200",
                            stuck ? "bg-frame" : "bg-base-200"
                        )}
                    >
                        {/* The fade masks this row only, so the bar's shade and border stay whole */}
                        <div ref={tabsScrollRef} className="edge-fade overflow-x-auto overflow-y-hidden no-scrollbar">
                            {/* The heading's column, so the first tab's label lines up with the heading above it */}
                            <div className="px-4 lg:px-10">
                                <div className="max-w-screen-lg mx-auto">
                                    <ul ref={tabListRef} className="relative flex w-max -ml-3 sm:-ml-4 text-sm font-semibold">
                                        {underline && (
                                            <motion.li
                                                aria-hidden="true"
                                                className="absolute bottom-0 left-0 h-[3px] rounded-t-full bg-primary dark:bg-base-content pointer-events-none"
                                                initial={false}
                                                animate={underline}
                                                transition={pillSpring}
                                            />
                                        )}

                                        {props.tabs.map((tab) => (
                                            <li key={tab.to} className="shrink-0">
                                                {tab.disabled ? (
                                                    <span className="inline-flex items-center gap-2 h-12 px-3 sm:px-4 text-base-content/40 cursor-not-allowed">
                                                        {tab.icon && <tab.icon className="hidden sm:inline w-5 h-5" />}
                                                        {tab.label}
                                                        {tab.count !== undefined && <TabCount count={tab.count} />}
                                                    </span>
                                                ) : (
                                                    <NavLink
                                                        to={tab.to}
                                                        end={tab.end}
                                                        preventScrollReset
                                                        onClick={onTabClick}
                                                        className={({ isActive }) =>
                                                            classNames(
                                                                "relative inline-flex items-center gap-2 h-12 px-3 sm:px-4 transition-colors",
                                                                isActive ? "text-primary dark:text-base-content" : "text-base-content/70 hover:bg-base-content/5"
                                                            )
                                                        }
                                                    >
                                                        {({ isActive }) => (
                                                            <>
                                                                {isActive && (
                                                                    <Helmet>
                                                                        <title>
                                                                            {tab.label}
                                                                            {(props.plainTextHeading && ` - ${props.plainTextHeading}`) ||
                                                                                (typeof props.heading === "string" && ` - ${props.heading}`)}
                                                                        </title>
                                                                    </Helmet>
                                                                )}

                                                                {/* Left out on a phone, so more of the row fits */}
                                                                {tab.icon && <tab.icon className="hidden sm:inline w-5 h-5" />}
                                                                {tab.label}
                                                                {tab.count !== undefined && <TabCount count={tab.count} />}
                                                            </>
                                                        )}
                                                    </NavLink>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </nav>
                </>
            )}

            {(typeof props.heading === "string" || props.plainTextHeading !== undefined) && (
                <Helmet>
                    <title>{props.plainTextHeading ?? props.heading}</title>
                </Helmet>
            )}

            {/* Tall enough that a short tab can still sit with the header collapsed */}
            <main
                className="relative"
                style={showTabs ? { minHeight: "calc(100vh - var(--frame-top) - var(--frame-bottom))" } : undefined}
            >
                {props.fullWidth ? props.children : <CenteredLayout hideBackLink>{props.children}</CenteredLayout>}
            </main>
        </>
    );
};

interface HeadedLayoutHeadingProps {}

HeadedLayout.Heading = (props: PropsWithChildren<HeadedLayoutHeadingProps>) => (
    <Heading.H1 className="text-3xl lg:text-4xl font-bold tracking-tight">{props.children}</Heading.H1>
);
